/**
 * Manual calendar — single slot picker using FullCalendar directly.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import moment from 'moment-timezone';
import type { EventInput } from '@fullcalendar/core';
import type { CalendarHandle } from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';
import { CalendarToolbar } from './CalendarToolbar';
import {
  AvailableSlotContent,
  BookedEventContent,
  BusyEventContent,
} from './events';
import {
  useCalendarEventHandlers,
  useManualCalendarEvents,
  type ManualCalendarExternalEvent,
} from './hooks';
import {
  busyEventChunkBounds,
  getScrollTimeForNow,
  getScrollTime,
  MAX_DURATION_MINUTES,
} from './events';
import type { ScheduleSlot } from '../state/useScheduleSharedState';
import './calendar.css';

interface ManualCalendarProps {
  duration: number;
  timezone?: string;
  interviewName?: string;
  candidateName?: string;
  bookedSlot?: ScheduleSlot | null;
  externalEvents?: ManualCalendarExternalEvent[];
  /** Extra FullCalendar events the parent wants to render on top (available slots, notice overlay). */
  overlayEvents?: EventInput[];
  onSlotSelect: (_slot: { start: string; end: string }) => void;
  onTimezoneChange?: (_tz: string) => void;
  /** Fired when the visible week changes — for calendar busy fetch window */
  onActiveRangeChange?: (_range: { start: string; end: string }) => void;
  isEdit?: boolean;
  goToDate?: Date | string;
}

export const ManualCalendar = forwardRef<CalendarHandle, ManualCalendarProps>(
  (
    {
      duration,
      timezone: timezoneProp,
      interviewName,
      candidateName,
      bookedSlot,
      externalEvents = [],
      overlayEvents = [],
      onSlotSelect,
      onTimezoneChange,
      onActiveRangeChange,
      isEdit = false,
      goToDate,
    },
    ref
  ) => {
    const calendarRef = useRef<FullCalendar>(null);
    const [timezone, setTimezone] = useState(
      () => timezoneProp || moment.tz.guess()
    );

    // Ref-based callback for toolbar to subscribe to datesSet without re-rendering
    const onDatesSetRef = useRef<(() => void) | null>(null);

    const handleTimezoneChange = useCallback(
      (tz: string) => {
        setTimezone(tz);
        onTimezoneChange?.(tz);
      },
      [onTimezoneChange]
    );

    useImperativeHandle(ref, () => ({
      goToDate: (date) =>
        calendarRef.current
          ?.getApi()
          ?.gotoDate(typeof date === 'string' ? new Date(date) : date),
      next: () => calendarRef.current?.getApi()?.next(),
      prev: () => calendarRef.current?.getApi()?.prev(),
      today: () => calendarRef.current?.getApi()?.today(),
      getTimezone: () => timezone,
      setTimezone: (tz) => handleTimezoneChange(tz),
      clearAll: () => {},
      getSlots: () =>
        bookedSlot
          ? [
              {
                id: 'booked',
                start: bookedSlot.start,
                end: bookedSlot.end,
                date: bookedSlot.start.split('T')[0],
              },
            ]
          : [],
    }));

    // In edit mode the booked slot is seeded from payloadRef via an async
    // useEffect in DetailsStep, so `bookedSlot` is null on the very first
    // render and FullCalendar commits to `initialDate=undefined` (= today).
    // Navigate imperatively once the slot arrives so the recruiter always
    // lands on the booked event's week, scrolled to its time position.
    const didNavigateRef = useRef(false);
    useEffect(() => {
      if (!isEdit || didNavigateRef.current || !bookedSlot?.start) return;
      const api = calendarRef.current?.getApi();
      if (!api) return;
      didNavigateRef.current = true;
      api.gotoDate(new Date(bookedSlot.start));
      api.scrollToTime(
        getScrollTime(moment.tz(bookedSlot.start, timezone).hour())
      );
    }, [isEdit, bookedSlot, timezone]);

    const { events: composedEvents } = useManualCalendarEvents({
      bookedSlot: bookedSlot ?? null,
      externalEvents,
      timezone,
      interviewName,
    });

    const events = overlayEvents.length
      ? [...composedEvents, ...overlayEvents]
      : composedEvents;

    const { handleSelect, handleEventDrop, handleEventResize } =
      useCalendarEventHandlers({
        calendarRef,
        timezone,
        duration,
        onSlotSelect,
      });

    const initialDate =
      isEdit && bookedSlot?.start
        ? new Date(bookedSlot.start)
        : goToDate
          ? typeof goToDate === 'string'
            ? new Date(goToDate)
            : goToDate
          : undefined;

    const renderEventContent = useCallback(
      (eventInfo: {
        event: {
          id: string;
          title: string;
          startStr: string;
          endStr: string;
          extendedProps: Record<string, unknown>;
        };
      }) => {
        const category = eventInfo.event.extendedProps?.category as string;
        if (category === 'booked') {
          return (
            <BookedEventContent
              interviewName={interviewName}
              candidateName={candidateName}
              start={eventInfo.event.startStr}
              end={eventInfo.event.endStr}
              timezone={timezone}
            />
          );
        }
        if (category === 'busy') {
          const tooltipTitle =
            (eventInfo.event.extendedProps?.tooltipTitle as
              | string
              | undefined) ?? undefined;
          const startMs = new Date(eventInfo.event.startStr).getTime();
          const endMs = new Date(eventInfo.event.endStr).getTime();
          return (
            <BusyEventContent
              title={eventInfo.event.title}
              tooltipTitle={tooltipTitle}
              start={eventInfo.event.startStr}
              end={eventInfo.event.endStr}
              timezone={timezone}
              onSlotClick={(e) => {
                const { chunkStartMs, chunkEndMs } = busyEventChunkBounds(
                  e.currentTarget,
                  startMs,
                  endMs,
                  timezone
                );
                const rect = e.currentTarget.getBoundingClientRect();
                const relY = Math.max(
                  0,
                  Math.min(rect.height, e.clientY - rect.top)
                );
                const proportion = rect.height > 0 ? relY / rect.height : 0;
                const clickMs =
                  chunkStartMs + proportion * (chunkEndMs - chunkStartMs);
                const gridMs = 15 * 60_000;
                const snapped = moment.tz(
                  Math.round(clickMs / gridMs) * gridMs,
                  timezone
                );
                if (snapped.isBefore(moment().tz(timezone))) return;
                onSlotSelect({
                  start: snapped.toISOString(),
                  end: snapped.clone().add(duration, 'minutes').toISOString(),
                });
                calendarRef.current?.getApi()?.unselect();
              }}
            />
          );
        }
        if (category === 'available') {
          return (
            <AvailableSlotContent
              startIso={eventInfo.event.startStr}
              endIso={eventInfo.event.endStr}
              duration={duration}
              timezone={timezone}
              onSelect={onSlotSelect}
            />
          );
        }
        return (
          <Box p="2px 4px" fontSize="11px" overflow="hidden">
            {eventInfo.event.title}
          </Box>
        );
      },
      [interviewName, candidateName, timezone, duration, onSlotSelect]
    );

    return (
      <Flex flexDir="column" h="100%" w="100%" className="im-calendar">
        <CalendarToolbar
          calendarRef={calendarRef}
          timezone={timezone}
          onTimezoneChange={handleTimezoneChange}
          disablePastNavigation
          onDatesSetRef={onDatesSetRef}
        />
        <Box
          flex="1"
          minH={0}
          position="relative"
          overflow="visible"
          className="im-calendar-fc-scroll-host"
        >
          <FullCalendar
            key={timezone}
            ref={calendarRef}
            plugins={[interactionPlugin, timeGridPlugin, momentTimezonePlugin]}
            nowIndicator={true}
            initialView="timeGridWeek"
            initialDate={initialDate}
            dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
            dayHeaderContent={(args) => {
              const date = moment(args.date);
              const isToday = date.isSame(moment(), 'day');
              return (
                <Flex
                  direction="column"
                  align="center"
                  py="6px"
                  color={isToday ? 'primary-500' : 'secondary-700'}
                >
                  <KText
                    fontSize="11px"
                    fontWeight="500"
                    textTransform="uppercase"
                  >
                    {date.format('ddd')}
                  </KText>
                  <KText fontSize="18px" fontWeight="600">
                    {date.format('D')}
                  </KText>
                </Flex>
              );
            }}
            headerToolbar={false}
            selectable={true}
            selectMirror={false}
            dayMaxEvents
            weekends
            height="100%"
            allDaySlot={false}
            slotDuration="00:15:00"
            snapDuration="00:15:00"
            slotLabelInterval={{ hours: 1 }}
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            expandRows={true}
            timeZone={timezone}
            firstDay={0}
            stickyHeaderDates={true}
            slotEventOverlap={true}
            scrollTime={getScrollTimeForNow(timezone)}
            eventOrderStrict={true}
            eventOrder="-extendedProps.priority,start_time"
            events={events}
            datesSet={(arg) => {
              onDatesSetRef.current?.();
              if (onActiveRangeChange && arg?.start && arg?.end) {
                const tz = timezone;
                onActiveRangeChange({
                  start: moment.tz(arg.start, tz).toISOString(),
                  end: moment.tz(arg.end, tz).toISOString(),
                });
              }
            }}
            select={handleSelect}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventContent={renderEventContent}
            selectAllow={(selectInfo) => {
              const now = moment().tz(timezone);
              const dur = moment
                .tz(selectInfo.endStr, timezone)
                .diff(moment.tz(selectInfo.startStr, timezone), 'minutes');
              return (
                selectInfo.start >= now.toDate() && dur <= MAX_DURATION_MINUTES
              );
            }}
            eventAllow={(dropInfo) =>
              dropInfo.start >= moment().tz(timezone).toDate()
            }
            slotLabelContent={({ text }) => (
              <KText variant="small_text" w="47px" alignItems="center">
                {text}
              </KText>
            )}
            slotLabelFormat={{
              hour: 'numeric',
              minute: '2-digit',
              omitZeroMinute: true,
              meridiem: true,
              separator: ' - ',
            }}
          />
        </Box>
      </Flex>
    );
  }
);

ManualCalendar.displayName = 'ManualCalendar';
